import { CHANNELS } from "../../shared/channels";
import { ClaudeUsage } from "../../shared/types";
import { call } from "../bridge";
import { byId } from "../dom";
import { onViewShown } from "../navigation";
import { setNeedle } from "./today_view";
import { locale, t } from "../i18n";

// this month's claude spend, estimated from the usage counts each answer carries

let consoleUrl = "https://console.anthropic.com/settings/billing";

function dollars(usd: number): string {
  return usd < 10 ? `$${usd.toFixed(3)}` : `$${usd.toFixed(2)}`;
}

function tokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}m`;
  if (count >= 1000) return `${Math.round(count / 1000)}k`;
  return String(count);
}

function setText(id: string, text: string): void {
  byId(id).textContent = text;
}

function paint(usage: ClaudeUsage): void {
  consoleUrl = usage.consoleUrl || consoleUrl;
  setText("claude-total", dollars(usage.totalUsd));
  setText("claude-chat", dollars(usage.chatUsd));
  setText("claude-tags", dollars(usage.tagsUsd));
  setText("claude-key", usage.keySet ? t("saved") : t("not set, add it in setup"));
  setText("claude-chat-model", usage.chatModel || "—");
  setText("claude-tag-model", usage.tagModel || "—");
  setText("claude-budget", usage.budgetUsd > 0
    ? t("{spent} of {budget} for tagging", { spent: dollars(usage.tagsUsd), budget: dollars(usage.budgetUsd) })
    : t("no cap on tagging"));
  const budget = usage.budgetUsd || 10;
  setNeedle("claude-total", usage.totalUsd / (budget * 2));
  setNeedle("claude-chat", usage.chatUsd / budget);
  setNeedle("claude-tags", usage.tagsUsd / budget);
  const fill = byId("claude-budget-fill");
  const used = usage.budgetUsd > 0 ? Math.min(1, usage.tagsUsd / usage.budgetUsd) : 0;
  fill.style.width = `calc(${(used * 100).toFixed(1)}% - 2px)`;
  fill.parentElement?.classList.toggle("over", used >= 1);
  setText("claude-batch", usage.batch
    ? t("{count} refs waiting since {time}", { count: usage.batch.count, time: new Date(usage.batch.startedAt).toLocaleTimeString(locale()) })
    : t("none running"));

  const rows = usage.rows.map((row) => {
    const line = document.createElement("div");
    line.className = "claude-row";
    const name = document.createElement("span");
    name.textContent = `${row.kind} · ${row.model}`;
    const cost = document.createElement("strong");
    cost.textContent = dollars(row.costUsd);
    const detail = document.createElement("small");
    detail.textContent = t("{calls} calls · {input} in · {cached} cached · {output} out", { calls: row.requests, input: tokens(row.inputTokens), cached: tokens(row.cacheReadTokens), output: tokens(row.outputTokens) });
    line.append(name, cost, detail);
    return line;
  });
  if (!rows.length) {
    const empty = document.createElement("span");
    empty.className = "scribble small";
    empty.textContent = t("nothing yet for {month}", { month: usage.month });
    rows.push(empty as HTMLDivElement);
  }
  byId("claude-rows").replaceChildren(...rows);

  // everyone's chi runs on this key, so the bill splits by who asked
  const people = (usage.people || []).map((person) => {
    const line = document.createElement("div");
    line.className = "claude-row";
    const name = document.createElement("span");
    name.textContent = person.name;
    const cost = document.createElement("strong");
    cost.textContent = dollars(person.usd);
    const detail = document.createElement("small");
    detail.textContent = t("{calls} calls", { calls: person.requests });
    line.append(name, cost, detail);
    return line;
  });
  if (!people.length) {
    const empty = document.createElement("span");
    empty.className = "scribble small";
    empty.textContent = t("split starts with the next answer");
    people.push(empty as HTMLDivElement);
  }
  byId("claude-people").replaceChildren(...people);
}

async function reload(): Promise<void> {
  try {
    paint(await call<ClaudeUsage>(CHANNELS.claudeUsage));
  } catch (error) {
    setText("claude-status", error instanceof Error ? error.message : t("could not read claude usage"));
  }
}

export async function setupClaudeView(): Promise<void> {
  byId("claude-refresh").addEventListener("click", () => void reload());
  byId("claude-open").addEventListener("click", () => void call(CHANNELS.openExternal, consoleUrl));
  onViewShown("claude", () => void reload());
}
